import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Store } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '../../../../components/design-system/admin/StatusBadge';

interface VendorPageHeaderProps {
    /** The vendor as saved (the header shows what the vendor is, not the unsaved edits). */
    vendor: any;
    /** undefined while unknown (hidden), null when the vendor has no plan. */
    planName?: string | null;
    loading?: boolean;
    actions?: ReactNode;
}

/** A vendor's page heading: logo, name, @alias, whether they can sign in, and their plan. */
export function VendorPageHeader({ vendor, planName, loading, actions }: VendorPageHeaderProps) {
    const { t } = useTranslation();
    const enabled = vendor?.isEnabled !== false;
    const logo = vendor?.config?.logo;

    return (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-testid="vendor-header">
            <div className="flex min-w-0 items-center gap-4">
                <div className="grid size-14 shrink-0 place-items-center overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10">
                    {loading ? (
                        <Skeleton className="size-full rounded-none" />
                    ) : logo ? (
                        <img
                            src={logo}
                            alt={t('ADMIN_VENDOREDIT.LOGO.ALT', { name: vendor?.name || '' })}
                            className="max-h-full max-w-full object-contain p-1.5"
                        />
                    ) : (
                        <Store className="size-5 text-muted-foreground" aria-hidden />
                    )}
                </div>
                <div className="min-w-0">
                    {loading ? (
                        <>
                            <Skeleton className="h-7 w-48" />
                            <Skeleton className="mt-2 h-4 w-64" />
                        </>
                    ) : (
                        <>
                            <h1 className="truncate font-heading text-2xl font-medium tracking-tight">{vendor?.name || '—'}</h1>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                                {vendor?.alias && <span dir="ltr">@{vendor.alias}</span>}
                                <StatusBadge tone={enabled ? 'success' : 'neutral'}>
                                    {enabled ? t('ADMIN_SHELL.COMMON.ENABLED') : t('ADMIN_SHELL.COMMON.DISABLED')}
                                </StatusBadge>
                                {planName !== undefined && (
                                    <span data-testid="vendor-header-plan">
                                        {planName ? t('ADMIN_VENDOREDIT.HEADER.PLAN', { name: planName }) : t('ADMIN_VENDOREDIT.HEADER.NO_PLAN')}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
    );
}
